import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import rawAxios from "axios";

import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import axios from "../../../utils/api-client";
import {
	BusStop,
	ClearMyDefaultBusStopPayload,
	CreateBusStopPayload,
	CreateTransportRequestPayload,
	DefaultBusStop,
	GetBusStopsPayload,
	GetMyDefaultBusStopPayload,
	GetMyLocationBusStopsPayload,
	GetMyTransportRequestsPayload,
	GetTransportDailyOrderPayload,
	GetTransportOptionsPayload,
	GetTransportRequestsPayload,
	RenameBusStopPayload,
	SetMyDefaultBusStopPayload,
	SupervisorTransportRequest,
	TransportActionPayload,
	TransportActionResult,
	TransportDailyOrderRow,
	TransportLocation,
	TransportOptions,
	TransportRequest,
	TransportState,
	UpdateBusStopStatusPayload,
} from "./transport-types";

// The worker app authenticates with its own token and must not go through the
// supervisor interceptor, which would refresh and redirect against the wrong
// session. Same shape as every other worker-side call in web-worker-slice.
const workerRequestConfig = () => ({
	headers: {
		"Content-Type": "application/json",
		Authorization: localStorage.getItem("worker-token") as string,
	},
});

const workerAxios = () => rawAxios.create();

const DEFAULT_ERROR_MESSAGE = "An error occurred. Please try again.";

const initialState: TransportState = {
	busStops: [],
	busStopsLocationId: null,
	getBusStops: {
		status: "idle",
		errorMessage: "",
	},
	createBusStop: {
		status: "idle",
		errorMessage: "",
	},
	renameBusStop: {
		status: "idle",
		errorMessage: "",
	},
	updateBusStopStatus: {
		status: "idle",
		errorMessage: "",
	},

	isTransportEnabledForWorker: null,
	shiftTransportOptions: null,
	shiftTransportOptionsShiftId: null,
	getShiftTransportOptions: {
		status: "idle",
		errorMessage: "",
	},
	myTransportRequests: [],
	getMyTransportRequests: {
		status: "idle",
		errorMessage: "",
	},
	cancelMyTransportRequest: {
		status: "idle",
		errorMessage: "",
	},
	cancellingRequestId: null,

	standaloneTransportOptions: null,
	getStandaloneTransportOptions: {
		status: "idle",
		errorMessage: "",
	},
	createTransportRequest: {
		status: "idle",
		errorMessage: "",
	},
	createdRequestCount: 0,

	myLocationId: null,
	myLocationBusStops: [],
	getMyLocationBusStops: {
		status: "idle",
		errorMessage: "",
	},
	myDefaultBusStop: null,
	getMyDefaultBusStop: {
		status: "idle",
		errorMessage: "",
	},
	setMyDefaultBusStop: {
		status: "idle",
		errorMessage: "",
	},
	clearMyDefaultBusStop: {
		status: "idle",
		errorMessage: "",
	},

	transportRequests: [],
	getTransportRequests: {
		status: "idle",
		errorMessage: "",
	},
	transportRequestsForbidden: false,
	actionTransportRequests: {
		status: "idle",
		errorMessage: "",
	},
	actionFailures: {},
	dailyOrder: [],
	getDailyOrder: {
		status: "idle",
		errorMessage: "",
	},
	transportLocations: [],
	getTransportLocations: {
		status: "idle",
		errorMessage: "",
	},
};

// Server errors arrive as { message } already localised by the API, so the
// message is shown as-is rather than mapped to a client string.
const getErrorMessage = (error: any) =>
	error?.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;

export const getBusStops = createAsyncThunk(
	"transport/get-bus-stops",
	async (
		{ locationId, includeInactive = true }: GetBusStopsPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/locations/${locationId}/transport/stops`,
				{ params: { includeInactive } }
			);
			// A location with no stops yet answers 200 with [], never 404.
			return fulfillWithValue({
				locationId,
				busStops: (res.data ?? []) as BusStop[],
			});
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const createBusStop = createAsyncThunk(
	"transport/create-bus-stop",
	async (
		{ locationId, name }: CreateBusStopPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/locations/${locationId}/transport/stops`,
				{ name: name.trim() }
			);
			await dispatch(getBusStops({ locationId }));
			return fulfillWithValue(res.data as BusStop);
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const renameBusStop = createAsyncThunk(
	"transport/rename-bus-stop",
	async (
		{ id, name, locationId }: RenameBusStopPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			// 200 with an empty body, so the list is refetched rather than patched.
			await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/transport/stops/${id}`,
				{ name: name.trim() }
			);
			await dispatch(getBusStops({ locationId }));
			return fulfillWithValue({ id, name: name.trim() });
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const updateBusStopStatus = createAsyncThunk(
	"transport/update-bus-stop-status",
	async (
		{ id, isActive, locationId }: UpdateBusStopStatusPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/transport/stops/${id}/status`,
				{ isActive }
			);
			await dispatch(getBusStops({ locationId }));
			return fulfillWithValue({ id, isActive });
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// ---------------------------------------------------------------------------
// Worker
// ---------------------------------------------------------------------------

// Whether the worker's own location does transport at all, asked once per
// session. Anything other than a clear yes is treated as "no transport here":
// an API without these endpoints must leave the worker app exactly as it was.
export const checkWorkerTransportEnabled = createAsyncThunk(
	"transport/check-worker-enabled",
	async (_payload: void, { fulfillWithValue }) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/options`,
				workerRequestConfig()
			);
			return fulfillWithValue(res.data?.transportEnabled === true);
		} catch (error: any) {
			return fulfillWithValue(false);
		}
	}
);

// Everything the transport step needs, for the shift the worker is about to
// sign up for. Called before the sign-up, never cached: it is what tells the
// app whether to show the step at all.
export const getShiftTransportOptions = createAsyncThunk(
	"transport/get-shift-options",
	async (
		{ shiftId, workerShiftId, locationId }: GetTransportOptionsPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/options`,
				{
					...workerRequestConfig(),
					params: { shiftId, workerShiftId, locationId },
				}
			);
			return fulfillWithValue({
				shiftId: shiftId ?? null,
				options: res.data as TransportOptions,
			});
		} catch (error: any) {
			// A failure here must not block the sign-up, so the caller treats a
			// rejection as "no transport step" rather than as an error to show.
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const getMyTransportRequests = createAsyncThunk(
	"transport/get-my-requests",
	async (
		{ from, to, status }: GetMyTransportRequestsPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/requests/mine`,
				{
					...workerRequestConfig(),
					params: { from, to, status },
				}
			);
			return fulfillWithValue((res.data ?? []) as TransportRequest[]);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Cancels one leg. A worker who booked both directions and no longer needs the
// ride home cancels that booking and keeps the ride in.
export const cancelMyTransportRequest = createAsyncThunk(
	"transport/cancel-my-request",
	async (
		{ id, refetch }: { id: number; refetch?: GetMyTransportRequestsPayload },
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			await workerAxios().delete(
				`${process.env.REACT_APP_API_END_POINT}/transport/requests/${id}`,
				workerRequestConfig()
			);
			// The ride is marked cancelled rather than removed, so the list is
			// refetched instead of patched — and with the window the screen is
			// currently showing, not the default one, or cancelling from the
			// past list would silently swap it for the upcoming one.
			await dispatch(getMyTransportRequests(refetch ?? {}));
			return fulfillWithValue(id);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Everything the standalone booking screen needs, for the worker's own
// location: whether transport runs there, the stops, the directions on offer
// and which stop to suggest. Called with no shift, so `transportTypes` comes
// back unnarrowed and is a real picker here rather than a statement.
export const getStandaloneTransportOptions = createAsyncThunk(
	"transport/get-standalone-options",
	async (
		{ locationId }: GetMyLocationBusStopsPayload = {},
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/options`,
				{ ...workerRequestConfig(), params: { locationId } }
			);
			return fulfillWithValue(res.data as TransportOptions);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Books a ride with no shift behind it — the car-in-the-shop case, or a Sunday.
// All or nothing: if one ride in the list is invalid the server creates none,
// so a ten-day booking never half-succeeds and the screen has one answer to
// show. "B" returns two rides per date.
export const createTransportRequest = createAsyncThunk(
	"transport/create-request",
	async (
		payload: CreateTransportRequestPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().post(
				`${process.env.REACT_APP_API_END_POINT}/transport/requests`,
				payload,
				workerRequestConfig()
			);
			return fulfillWithValue((res.data ?? []) as TransportRequest[]);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// The bus stops for the worker's own location, for the default-bus-stop
// settings screen. Separate from getShiftTransportOptions, which is scoped to
// the shift being signed up for and cleared between shifts.
export const getMyLocationBusStops = createAsyncThunk(
	"transport/get-my-location-bus-stops",
	async (
		{ locationId }: GetMyLocationBusStopsPayload = {},
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/options`,
				{ ...workerRequestConfig(), params: { locationId } }
			);
			return fulfillWithValue({
				locationId: (res.data?.locationId ?? locationId ?? null) as
					| number
					| null,
				busStops: (res.data?.busStops ?? []) as BusStop[],
			});
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Reads the worker's usual stop for a location. A 204 means none is set yet —
// a normal state, not an error.
export const getMyDefaultBusStop = createAsyncThunk(
	"transport/get-my-default",
	async (
		{ locationId }: GetMyDefaultBusStopPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await workerAxios().get(
				`${process.env.REACT_APP_API_END_POINT}/transport/my-default`,
				{ ...workerRequestConfig(), params: { locationId } }
			);
			if (res.status === 204) {
				return fulfillWithValue(null);
			}
			return fulfillWithValue(res.data as DefaultBusStop);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Upsert — there is only ever one default per worker per location.
export const setMyDefaultBusStop = createAsyncThunk(
	"transport/set-my-default",
	async (
		{ locationId, busStopId }: SetMyDefaultBusStopPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			await workerAxios().put(
				`${process.env.REACT_APP_API_END_POINT}/transport/my-default`,
				{ busStopId },
				{ ...workerRequestConfig(), params: { locationId } }
			);
			await dispatch(getMyDefaultBusStop({ locationId }));
			return fulfillWithValue({ locationId, busStopId });
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const clearMyDefaultBusStop = createAsyncThunk(
	"transport/clear-my-default",
	async (
		{ locationId }: ClearMyDefaultBusStopPayload,
		{ fulfillWithValue, rejectWithValue }
	) => {
		try {
			await workerAxios().delete(
				`${process.env.REACT_APP_API_END_POINT}/transport/my-default`,
				{ ...workerRequestConfig(), params: { locationId } }
			);
			return fulfillWithValue(locationId);
		} catch (error: any) {
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// ---------------------------------------------------------------------------
// Supervisor and ops
// ---------------------------------------------------------------------------

export const getTransportRequests = createAsyncThunk(
	"transport/get-requests",
	async (
		{ locationId, from, to, status }: GetTransportRequestsPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/transport/requests`,
				{ params: { locationId, from, to, status } }
			);
			return fulfillWithValue({
				requests: (res.data ?? []) as SupervisorTransportRequest[],
				isForbidden: false,
			});
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			// A location the caller cannot reach now answers 403 rather than
			// returning the data, so the screen can say "not your location"
			// instead of showing an empty day.
			if (error?.response?.status === 403) {
				return fulfillWithValue({
					requests: [] as SupervisorTransportRequest[],
					isForbidden: true,
				});
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// Approves, rejects or cancels rides, and on approval sets the time the car
// comes. Batch is the normal shape: the real action is "everyone on tomorrow's
// entrada leaves at 04:40", and a single decision is a one-item list.
//
// A 200 does not mean every ride was decided. Rides the supervisor cannot act
// on land in `failed` and the rest still apply, so the caller reconciles from
// `updated` and surfaces `failed` per row.
export const actionTransportRequests = createAsyncThunk(
	"transport/action-requests",
	async (
		payload: TransportActionPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/transport/requests/actions`,
				payload
			);
			return fulfillWithValue(res.data as TransportActionResult);
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			Sentry.captureException(error);
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

// The order handed to the transport supplier: every ride at one location on one
// day, in dispatch order.
export const getTransportDailyOrder = createAsyncThunk(
	"transport/get-daily-order",
	async (
		{ locationId, date, status }: GetTransportDailyOrderPayload,
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Reports/GetTransportDailyOrder`,
				{ params: { locationId, date, status } }
			);
			// 204 means no rides that day, and arrives with an empty body.
			if (res.status === 204) {
				return fulfillWithValue([] as TransportDailyOrderRow[]);
			}
			return fulfillWithValue((res.data ?? []) as TransportDailyOrderRow[]);
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const getTransportLocations = createAsyncThunk(
	"transport/get-locations",
	async (
		_payload: void,
		{ dispatch, fulfillWithValue, getState, rejectWithValue }
	) => {
		try {
			const userId = (getState() as any).user?.userData?.id;
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Locations/GetLocationsByUser?userid=${userId}`
			);
			if (res.status === 204) {
				return fulfillWithValue([] as TransportLocation[]);
			}
			return fulfillWithValue((res.data ?? []) as TransportLocation[]);
		} catch (error: any) {
			if (error?.response?.status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue(getErrorMessage(error));
		}
	}
);

export const TransportSlice = createSlice({
	name: "transport",
	initialState,
	reducers: {
		resetCreateBusStop: (state) => {
			state.createBusStop.status = "idle";
			state.createBusStop.errorMessage = "";
		},
		resetRenameBusStop: (state) => {
			state.renameBusStop.status = "idle";
			state.renameBusStop.errorMessage = "";
		},
		resetUpdateBusStopStatus: (state) => {
			state.updateBusStopStatus.status = "idle";
			state.updateBusStopStatus.errorMessage = "";
		},
		// Called when the transport step closes, so the next shift starts from
		// nothing rather than from the previous shift's stops and suggestion.
		clearShiftTransportOptions: (state) => {
			state.shiftTransportOptions = null;
			state.shiftTransportOptionsShiftId = null;
			state.getShiftTransportOptions.status = "idle";
			state.getShiftTransportOptions.errorMessage = "";
		},
		resetCancelMyTransportRequest: (state) => {
			state.cancelMyTransportRequest.status = "idle";
			state.cancelMyTransportRequest.errorMessage = "";
			state.cancellingRequestId = null;
		},
		// Called when the standalone booking screen closes, so a booking made
		// now cannot flash a stale confirmation the next time it opens.
		resetCreateTransportRequest: (state) => {
			state.createTransportRequest.status = "idle";
			state.createTransportRequest.errorMessage = "";
			state.createdRequestCount = 0;
		},
		// Called when the decision dialog closes, so the next one opens clean
		// and a stale per-row failure never outlives the list it described.
		resetActionTransportRequests: (state) => {
			state.actionTransportRequests.status = "idle";
			state.actionTransportRequests.errorMessage = "";
			state.actionFailures = {};
		},
		resetSetMyDefaultBusStop: (state) => {
			state.setMyDefaultBusStop.status = "idle";
			state.setMyDefaultBusStop.errorMessage = "";
		},
		resetClearMyDefaultBusStop: (state) => {
			state.clearMyDefaultBusStop.status = "idle";
			state.clearMyDefaultBusStop.errorMessage = "";
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getBusStops.pending, (state) => {
			state.getBusStops.status = "pending";
			state.getBusStops.errorMessage = "";
		});
		builder.addCase(getBusStops.fulfilled, (state, action) => {
			state.getBusStops.status = "fulfilled";
			state.getBusStops.errorMessage = "";
			state.busStops = action.payload.busStops;
			state.busStopsLocationId = action.payload.locationId;
		});
		builder.addCase(getBusStops.rejected, (state, action: any) => {
			state.getBusStops.status = "error";
			state.getBusStops.errorMessage = action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.busStops = [];
		});

		builder.addCase(createBusStop.pending, (state) => {
			state.createBusStop.status = "pending";
			state.createBusStop.errorMessage = "";
		});
		builder.addCase(createBusStop.fulfilled, (state) => {
			state.createBusStop.status = "fulfilled";
			state.createBusStop.errorMessage = "";
		});
		builder.addCase(createBusStop.rejected, (state, action: any) => {
			state.createBusStop.status = "error";
			state.createBusStop.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(renameBusStop.pending, (state) => {
			state.renameBusStop.status = "pending";
			state.renameBusStop.errorMessage = "";
		});
		builder.addCase(renameBusStop.fulfilled, (state) => {
			state.renameBusStop.status = "fulfilled";
			state.renameBusStop.errorMessage = "";
		});
		builder.addCase(renameBusStop.rejected, (state, action: any) => {
			state.renameBusStop.status = "error";
			state.renameBusStop.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(updateBusStopStatus.pending, (state) => {
			state.updateBusStopStatus.status = "pending";
			state.updateBusStopStatus.errorMessage = "";
		});
		builder.addCase(updateBusStopStatus.fulfilled, (state) => {
			state.updateBusStopStatus.status = "fulfilled";
			state.updateBusStopStatus.errorMessage = "";
		});
		builder.addCase(updateBusStopStatus.rejected, (state, action: any) => {
			state.updateBusStopStatus.status = "error";
			state.updateBusStopStatus.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(checkWorkerTransportEnabled.fulfilled, (state, action) => {
			state.isTransportEnabledForWorker = action.payload;
		});

		builder.addCase(getShiftTransportOptions.pending, (state) => {
			state.getShiftTransportOptions.status = "pending";
			state.getShiftTransportOptions.errorMessage = "";
		});
		builder.addCase(getShiftTransportOptions.fulfilled, (state, action) => {
			state.getShiftTransportOptions.status = "fulfilled";
			state.getShiftTransportOptions.errorMessage = "";
			state.shiftTransportOptions = action.payload.options;
			state.shiftTransportOptionsShiftId = action.payload.shiftId;
		});
		builder.addCase(getShiftTransportOptions.rejected, (state, action: any) => {
			state.getShiftTransportOptions.status = "error";
			state.getShiftTransportOptions.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.shiftTransportOptions = null;
			state.shiftTransportOptionsShiftId = null;
		});

		builder.addCase(getMyTransportRequests.pending, (state) => {
			state.getMyTransportRequests.status = "pending";
			state.getMyTransportRequests.errorMessage = "";
		});
		builder.addCase(getMyTransportRequests.fulfilled, (state, action) => {
			state.getMyTransportRequests.status = "fulfilled";
			state.getMyTransportRequests.errorMessage = "";
			state.myTransportRequests = action.payload;
		});
		builder.addCase(getMyTransportRequests.rejected, (state, action: any) => {
			state.getMyTransportRequests.status = "error";
			state.getMyTransportRequests.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.myTransportRequests = [];
		});

		builder.addCase(cancelMyTransportRequest.pending, (state, action) => {
			state.cancelMyTransportRequest.status = "pending";
			state.cancelMyTransportRequest.errorMessage = "";
			state.cancellingRequestId = action.meta.arg.id;
		});
		builder.addCase(cancelMyTransportRequest.fulfilled, (state) => {
			state.cancelMyTransportRequest.status = "fulfilled";
			state.cancelMyTransportRequest.errorMessage = "";
			state.cancellingRequestId = null;
		});
		builder.addCase(cancelMyTransportRequest.rejected, (state, action: any) => {
			state.cancelMyTransportRequest.status = "error";
			state.cancelMyTransportRequest.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.cancellingRequestId = null;
		});

		builder.addCase(getStandaloneTransportOptions.pending, (state) => {
			state.getStandaloneTransportOptions.status = "pending";
			state.getStandaloneTransportOptions.errorMessage = "";
		});
		builder.addCase(
			getStandaloneTransportOptions.fulfilled,
			(state, action) => {
				state.getStandaloneTransportOptions.status = "fulfilled";
				state.getStandaloneTransportOptions.errorMessage = "";
				state.standaloneTransportOptions = action.payload;
			}
		);
		builder.addCase(
			getStandaloneTransportOptions.rejected,
			(state, action: any) => {
				state.getStandaloneTransportOptions.status = "error";
				state.getStandaloneTransportOptions.errorMessage =
					action.payload ?? DEFAULT_ERROR_MESSAGE;
				state.standaloneTransportOptions = null;
			}
		);

		builder.addCase(createTransportRequest.pending, (state) => {
			state.createTransportRequest.status = "pending";
			state.createTransportRequest.errorMessage = "";
		});
		builder.addCase(createTransportRequest.fulfilled, (state, action) => {
			state.createTransportRequest.status = "fulfilled";
			state.createTransportRequest.errorMessage = "";
			state.createdRequestCount = action.payload.length;
		});
		builder.addCase(createTransportRequest.rejected, (state, action: any) => {
			state.createTransportRequest.status = "error";
			state.createTransportRequest.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.createdRequestCount = 0;
		});

		builder.addCase(getMyLocationBusStops.pending, (state) => {
			state.getMyLocationBusStops.status = "pending";
			state.getMyLocationBusStops.errorMessage = "";
		});
		builder.addCase(getMyLocationBusStops.fulfilled, (state, action) => {
			state.getMyLocationBusStops.status = "fulfilled";
			state.getMyLocationBusStops.errorMessage = "";
			state.myLocationId = action.payload.locationId;
			state.myLocationBusStops = action.payload.busStops;
		});
		builder.addCase(getMyLocationBusStops.rejected, (state, action: any) => {
			state.getMyLocationBusStops.status = "error";
			state.getMyLocationBusStops.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.myLocationBusStops = [];
		});

		builder.addCase(getMyDefaultBusStop.pending, (state) => {
			state.getMyDefaultBusStop.status = "pending";
			state.getMyDefaultBusStop.errorMessage = "";
		});
		builder.addCase(getMyDefaultBusStop.fulfilled, (state, action) => {
			state.getMyDefaultBusStop.status = "fulfilled";
			state.getMyDefaultBusStop.errorMessage = "";
			state.myDefaultBusStop = action.payload;
		});
		builder.addCase(getMyDefaultBusStop.rejected, (state, action: any) => {
			state.getMyDefaultBusStop.status = "error";
			state.getMyDefaultBusStop.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.myDefaultBusStop = null;
		});

		builder.addCase(setMyDefaultBusStop.pending, (state) => {
			state.setMyDefaultBusStop.status = "pending";
			state.setMyDefaultBusStop.errorMessage = "";
		});
		builder.addCase(setMyDefaultBusStop.fulfilled, (state) => {
			state.setMyDefaultBusStop.status = "fulfilled";
			state.setMyDefaultBusStop.errorMessage = "";
		});
		builder.addCase(setMyDefaultBusStop.rejected, (state, action: any) => {
			state.setMyDefaultBusStop.status = "error";
			state.setMyDefaultBusStop.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(clearMyDefaultBusStop.pending, (state) => {
			state.clearMyDefaultBusStop.status = "pending";
			state.clearMyDefaultBusStop.errorMessage = "";
		});
		builder.addCase(clearMyDefaultBusStop.fulfilled, (state) => {
			state.clearMyDefaultBusStop.status = "fulfilled";
			state.clearMyDefaultBusStop.errorMessage = "";
			state.myDefaultBusStop = null;
		});
		builder.addCase(clearMyDefaultBusStop.rejected, (state, action: any) => {
			state.clearMyDefaultBusStop.status = "error";
			state.clearMyDefaultBusStop.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(getTransportRequests.pending, (state) => {
			state.getTransportRequests.status = "pending";
			state.getTransportRequests.errorMessage = "";
		});
		builder.addCase(getTransportRequests.fulfilled, (state, action) => {
			state.getTransportRequests.status = "fulfilled";
			state.getTransportRequests.errorMessage = "";
			state.transportRequests = action.payload.requests;
			state.transportRequestsForbidden = action.payload.isForbidden;
			// A fresh list is the new truth, so per-row failures from an
			// earlier decision no longer describe anything on screen.
			state.actionFailures = {};
		});
		builder.addCase(getTransportRequests.rejected, (state, action: any) => {
			state.getTransportRequests.status = "error";
			state.getTransportRequests.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.transportRequests = [];
			state.transportRequestsForbidden = false;
		});

		builder.addCase(actionTransportRequests.pending, (state) => {
			state.actionTransportRequests.status = "pending";
			state.actionTransportRequests.errorMessage = "";
			state.actionFailures = {};
		});
		builder.addCase(actionTransportRequests.fulfilled, (state, action) => {
			state.actionTransportRequests.status = "fulfilled";
			state.actionTransportRequests.errorMessage = "";
			// Patched in place from `updated` rather than re-fetched, so the
			// supervisor's scroll position and selection survive a decision.
			// Merged rather than swapped: `updated` carries the ride without the
			// rider, so replacing the row outright would blank the worker's name
			// and employee id the moment it was decided.
			const updatedById = new Map(
				action.payload.updated.map((ride) => [ride.id, ride])
			);
			state.transportRequests = state.transportRequests.map((ride) => {
				const updated = updatedById.get(ride.id);
				return updated ? { ...ride, ...updated } : ride;
			});
			state.actionFailures = action.payload.failed.reduce(
				(failures: Record<number, string>, failure) => {
					failures[failure.id] = failure.reason;
					return failures;
				},
				{}
			);
		});
		builder.addCase(actionTransportRequests.rejected, (state, action: any) => {
			state.actionTransportRequests.status = "error";
			state.actionTransportRequests.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
		});

		builder.addCase(getTransportDailyOrder.pending, (state) => {
			state.getDailyOrder.status = "pending";
			state.getDailyOrder.errorMessage = "";
		});
		builder.addCase(getTransportDailyOrder.fulfilled, (state, action) => {
			state.getDailyOrder.status = "fulfilled";
			state.getDailyOrder.errorMessage = "";
			state.dailyOrder = action.payload;
		});
		builder.addCase(getTransportDailyOrder.rejected, (state, action: any) => {
			state.getDailyOrder.status = "error";
			state.getDailyOrder.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.dailyOrder = [];
		});

		builder.addCase(getTransportLocations.pending, (state) => {
			state.getTransportLocations.status = "pending";
			state.getTransportLocations.errorMessage = "";
		});
		builder.addCase(getTransportLocations.fulfilled, (state, action) => {
			state.getTransportLocations.status = "fulfilled";
			state.getTransportLocations.errorMessage = "";
			state.transportLocations = action.payload;
		});
		builder.addCase(getTransportLocations.rejected, (state, action: any) => {
			state.getTransportLocations.status = "error";
			state.getTransportLocations.errorMessage =
				action.payload ?? DEFAULT_ERROR_MESSAGE;
			state.transportLocations = [];
		});
	},
});

export const {
	clearShiftTransportOptions,
	resetActionTransportRequests,
	resetCancelMyTransportRequest,
	resetClearMyDefaultBusStop,
	resetCreateBusStop,
	resetCreateTransportRequest,
	resetRenameBusStop,
	resetSetMyDefaultBusStop,
	resetUpdateBusStopStatus,
} = TransportSlice.actions;

export default TransportSlice.reducer;
